import type { PilotSkillProgress } from './types';

export const pilotSkillLabels: Record<string, string> = {
  'BK-MTH-NUM-PV-002': '10 satuan = 1 puluhan',
  'BK-MTH-NUM-PV-003': 'Puluhan dan satuan',
  'BK-MTH-NUM-PV-005': 'Menyusun bilangan dua digit',
  'BK-MTH-NUM-PV-006': 'Mengurai bilangan dua digit',
  'BK-MTH-OPS-ADD-021': '2 digit + 1 digit tanpa regrouping',
  'BK-MTH-OPS-ADD-022': '2 digit + 2 digit tanpa regrouping',
  'BK-MTH-OPS-ADD-023': 'Konsep regrouping penjumlahan',
  'BK-MTH-OPS-ADD-025': '2 digit + 2 digit dengan regrouping',
  'BK-MTH-OPS-SUB-021': '2 digit − 1 digit tanpa regrouping',
  'BK-MTH-OPS-SUB-022': '2 digit − 2 digit tanpa regrouping',
  'BK-MTH-OPS-SUB-023': 'Konsep regrouping pengurangan',
  'BK-MTH-OPS-SUB-025': '2 digit − 2 digit dengan regrouping',
};

export const pilotSkillOrder = Object.keys(pilotSkillLabels);

export const pilotRequiredPrerequisites: Record<string, string[]> = {
  'BK-MTH-NUM-PV-002': [],
  'BK-MTH-NUM-PV-003': ['BK-MTH-NUM-PV-002'],
  'BK-MTH-NUM-PV-005': ['BK-MTH-NUM-PV-002', 'BK-MTH-NUM-PV-003'],
  'BK-MTH-NUM-PV-006': ['BK-MTH-NUM-PV-003'],
  'BK-MTH-OPS-ADD-021': ['BK-MTH-NUM-PV-003', 'BK-MTH-NUM-PV-005'],
  'BK-MTH-OPS-ADD-022': ['BK-MTH-NUM-PV-006', 'BK-MTH-OPS-ADD-021'],
  'BK-MTH-OPS-ADD-023': ['BK-MTH-NUM-PV-002', 'BK-MTH-NUM-PV-003', 'BK-MTH-NUM-PV-006', 'BK-MTH-OPS-ADD-021'],
  'BK-MTH-OPS-ADD-025': ['BK-MTH-OPS-ADD-022', 'BK-MTH-OPS-ADD-023', 'BK-MTH-NUM-PV-006'],
  'BK-MTH-OPS-SUB-021': ['BK-MTH-NUM-PV-003'],
  'BK-MTH-OPS-SUB-022': ['BK-MTH-NUM-PV-006', 'BK-MTH-OPS-SUB-021'],
  'BK-MTH-OPS-SUB-023': ['BK-MTH-NUM-PV-002', 'BK-MTH-NUM-PV-003', 'BK-MTH-NUM-PV-006', 'BK-MTH-OPS-SUB-021'],
  'BK-MTH-OPS-SUB-025': ['BK-MTH-OPS-SUB-022', 'BK-MTH-OPS-SUB-023', 'BK-MTH-NUM-PV-006'],
};

const baselineMastery: Record<string, number> = {
  'BK-MTH-NUM-PV-002': 92,
  'BK-MTH-NUM-PV-003': 90,
  'BK-MTH-NUM-PV-005': 88,
  'BK-MTH-NUM-PV-006': 86,
  'BK-MTH-OPS-ADD-021': 90,
  'BK-MTH-OPS-ADD-022': 87,
  'BK-MTH-OPS-ADD-023': 74,
  'BK-MTH-OPS-ADD-025': 58,
  'BK-MTH-OPS-SUB-021': 82,
  'BK-MTH-OPS-SUB-022': 76,
  'BK-MTH-OPS-SUB-023': 61,
  'BK-MTH-OPS-SUB-025': 46,
};

function masteryState(value: number): PilotSkillProgress['state'] {
  if (value >= 90) return 'provisional_mastery';
  if (value >= 75) return 'practicing';
  if (value >= 50) return 'learning';
  return 'available';
}

export function createBaselineProgress(): Record<string, PilotSkillProgress> {
  return Object.fromEntries(
    Object.entries(pilotSkillLabels).map(([skillCode, label]) => {
      const masteryPercent = baselineMastery[skillCode] ?? 0;
      return [skillCode, {
        skillCode,
        label,
        masteryPercent,
        state: masteryState(masteryPercent),
        evidenceCount: 0,
        firstAttemptCorrect: 0,
        finalCorrect: 0,
        reviewPasses: 0,
        misconceptionSignals: 0,
      } satisfies PilotSkillProgress];
    }),
  );
}

export function stateFromMastery(value: number, options?: { evidenceCount?: number; reviewPasses?: number }): PilotSkillProgress['state'] {
  if (value >= 90 && (options?.reviewPasses ?? 0) >= 1 && (options?.evidenceCount ?? 0) >= 6) return 'mastered';
  return masteryState(value);
}

export function prerequisitesMet(skillCode: string, progress: Record<string, PilotSkillProgress>, threshold = 65) {
  return (pilotRequiredPrerequisites[skillCode] ?? []).every((code) => (progress[code]?.masteryPercent ?? 0) >= threshold);
}
